import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { DiagnosticNotesSection } from './DiagnosticNotesSection';
import { ProcedureCptCodesSection } from './ProcedureCptCodesSection';
import { TreatmentCptCodesSection } from './TreatmentCptCodesSection';
import { EMCodeSection } from './EMCodeSection';
import type { ClinicalSummaryData } from '@/types/clinicalSummary';
import { useFeatureFlag, useOrganizationFeatureFlags } from '@/stores/organizationFeatureFlagsStore';

interface EnhancedCodingAssistantProps {
  appointmentId?: string;
  encounterId?: string;
  isEncounterMode?: boolean;
  appointment?: any;
  encounter?: any;
  refreshKey?: number;
  clinicalSummary?: ClinicalSummaryData | null;
  onGenerateCode?: (code: string, type: string) => void;
  onCodeStatusChange?: () => void;
}

/**
 * Tooltip component for section headers
 */
const InfoTooltip: React.FC<{ content: string }> = ({ content }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block ml-2">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(prev => !prev)}
        className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
      >
        <Info className="w-5 h-5" />
      </button>
      {show && (
        <div className="absolute z-50 w-64 sm:w-80 max-w-[calc(100vw-32px)] p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg -top-2 left-6">
          <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -left-1 top-3"></div>
          {content}
        </div>
      )}
    </div>
  );
};

/**
 * Enhanced Coding Assistant with Two-Layer CPT System
 *
 * This component provides diagnostic note creation, ICD-10 codes, and the two-layer CPT coding system:
 * - Layer 1: E&M codes (automatic, backend)
 * - Layer 2: Procedure codes (AI extraction with physician review)
 */
export const EnhancedCodingAssistant: React.FC<EnhancedCodingAssistantProps> = ({
  appointmentId,
  encounterId,
  isEncounterMode = false,
  clinicalSummary
}) => {
  // Extract treatment notes from clinical summary for CPT search suggestions
  const treatmentNotes = clinicalSummary?.aiGenerated?.treatment?.physicianNotes?.plainText || '';
  // Wait for the organization's flags before mounting E&M and CPT: they default to on, and a
  // standalone org (where both are off, AMP-142) would otherwise fire requests the API refuses.
  const flagsLoaded = useOrganizationFeatureFlags((s) => s.isLoaded);
  const emCodingAssistantEnabled = useFeatureFlag('emCodingAssistantEnabled') && flagsLoaded;
  const cptCodingAssistantEnabled = useFeatureFlag('cptCodingAssistantEnabled') && flagsLoaded;
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Section 1: Diagnostic Coding (ICD-10) — ALWAYS visible, independent of
          either coding-assistant flag. Diagnostics are the clinical core. */}
      <div className="border border-gray-200 rounded-lg p-4 lg:p-5 bg-white">
        <div className="mb-4 flex items-center">
          <h2 className="text-base font-semibold text-gray-900">Diagnostic Coding (ICD-10)</h2>
          <InfoTooltip content="Search for ICD-10 diagnosis codes to document patient conditions and symptoms" />
        </div>
        <DiagnosticNotesSection
          appointmentId={appointmentId}
          encounterId={encounterId}
          isEncounterMode={isEncounterMode}
        />
      </div>

      {/* Section 2: E&M Visit Code — gated by the per-org EM Coding Assistant flag. */}
      {emCodingAssistantEnabled && (
        <div className="border border-gray-200 rounded-lg p-4 lg:p-5 bg-white">
          <div className="mb-4 flex items-center">
            <h2 className="text-base font-semibold text-gray-900">E&M Visit Code</h2>
            <InfoTooltip content="Evaluation & Management code (99201-99215) for the visit itself. Based on visit complexity and Medical Decision Making (MDM) level following 2021 CPT guidelines." />
          </div>
          <EMCodeSection
            appointmentId={appointmentId}
            encounterId={encounterId}
            isEncounterMode={isEncounterMode}
          />
        </div>
      )}

      {/* Sections 3-4: Procedure + Additional CPT search — gated by the per-org
          CPT Coding Assistant flag. */}
      {cptCodingAssistantEnabled && (
        <>
          <div className="border border-gray-200 rounded-lg p-4 lg:p-5 bg-white">
            <div className="mb-4 flex items-center">
              <h2 className="text-base font-semibold text-gray-900">Procedure CPT Codes</h2>
              <InfoTooltip content="Procedures performed during the visit (injections, EKG, wound care, etc.) extracted from transcript and physician notes. Review and approve suggested codes." />
            </div>
            <ProcedureCptCodesSection
              appointmentId={appointmentId}
              encounterId={encounterId}
              isEncounterMode={isEncounterMode}
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4 lg:p-5 bg-white">
            <div className="mb-4 flex items-center">
              <h2 className="text-base font-semibold text-gray-900">Additional CPT Code Search</h2>
              <InfoTooltip content="Manually search for additional treatment or procedure codes not automatically detected." />
            </div>
            <TreatmentCptCodesSection
              appointmentId={appointmentId}
              encounterId={encounterId}
              isEncounterMode={isEncounterMode}
              treatmentNotes={treatmentNotes}
            />
          </div>
        </>
      )}
    </div>
  );
};
